import { isEmpty, sortBy } from 'lodash'
import {
  allUnits,
  distanceUnits,
  systemUnitToFullUnit,
  textMatchToUnit,
  timeTextMatchToUnit,
} from 'common/constants/unitConstants'

const sortUnitsByPriority = (units) => {
  const priority = [allUnits.reps, allUnits['lb/kg'], allUnits.time, ...Object.values(distanceUnits)]
  const sortedUnits = sortBy(units, (unit) => priority.indexOf(unit))
  return sortedUnits
}

const getDefaultUnitsFromInstr = (unitToValueFromInstr) => {
  if (!isEmpty(unitToValueFromInstr)) {
    const unitsFromInstr = Object.keys(unitToValueFromInstr || {})
    if (unitsFromInstr.length === 1) {
      // only 1 unit was extracted from instructions
      const firstUnit = unitsFromInstr[0]
      const units = [firstUnit]
      return units
    } else {
      // more than 1 unit was extracted from instructions, sort by priority
      const sortedUnitsByPrio = sortUnitsByPriority(unitsFromInstr)
      const firstUnit = sortedUnitsByPrio[0]
      const secondUnit = sortedUnitsByPrio[1]
      const units = [firstUnit, secondUnit]
      return units
    }
  } else {
    return []
  }
}

function createRegexFromUnits(units) {
  const unitPattern = units.join('|')
  return new RegExp(`(\\d+[.,]?\\d*)\\s*-?\\s*(${unitPattern})\\b`, 'gi')
}

const extractTimeValueFromText = ({ text, exTimer }) => {
  // if exercise timer is set, return timer value
  if (exTimer) {
    return exTimer
  }

  // otherwise try to derive the time from instructions
  const regex = createRegexFromUnits(Object.keys(timeTextMatchToUnit))
  let matches = {}
  let match

  while ((match = regex.exec(text)) !== null) {
    const textTimeMatch = match[2]
    const unit = timeTextMatchToUnit[String(textTimeMatch || '').toLowerCase()]
    const value = match[1].padStart(2, '0')
    if (unit && value) {
      matches[unit] = value
    }
  }

  let timeStr
  if (!isEmpty(matches)) {
    let hr = '00'
    let min = '00'
    let sec = '00'
    Object.entries(matches).forEach(([unit, unitValue]) => {
      if (unit === 'hr') {
        hr = unitValue
      } else if (unit === 'min') {
        min = unitValue
      } else if (unit === 'sec') {
        sec = unitValue
      }
    })
    timeStr = `${hr}:${min}:${sec}.00`
  }

  const timeRegex = /\b(\d{1,2}):(\d{2}):(\d{2})\b/g // Match HH:MM:SS format
  const timeStrMatch = timeRegex.exec(text)

  if (timeStrMatch) {
    const hr = timeStrMatch[1].padStart(2, '0') || '00'
    const min = timeStrMatch[2] || '00'
    const sec = timeStrMatch[3] || '00'
    timeStr = `${hr}:${min}:${sec}.00`
  }

  return timeStr
}

const getIsDistanceUnitAdded = ({ unitToValue, matchedFullUnit }) => {
  let unitToValueFound = false
  let matchedFullUnitFound = false
  Object.values(distanceUnits).forEach((unit) => {
    if (unitToValue?.[unit]) {
      unitToValueFound = true
    }

    if (matchedFullUnit === unit) {
      matchedFullUnitFound = true
    }
  })
  return unitToValueFound && matchedFullUnitFound
}

const extractUnitValuesFromText = ({ text, exTimer, exHasStopWatch }) => {
  if (!text) return { unitToValueFromInstr: {} }
  const MAX_EXTRACTED_UNIT_COUNT = 5 // Extract up to 5 units. Should be accurate enough. Also setting this to prevent unlikely infinite while loop.

  // Normalize text with lowercase & remove special characters. Allow slashes, commas for multi-value inputs
  /* eslint-disable-next-line no-useless-escape */
  const normalizedText = text.toLowerCase().replace(/[^a-z0-9:\-\/\,\.\s]/gi, '')

  let unitExtractedCount = 0
  let unitToValue = {}

  // Extract time first
  const time = extractTimeValueFromText({ text: normalizedText, exTimer })
  if (time || exHasStopWatch) {
    unitExtractedCount++
    unitToValue[allUnits.time] = true
  }

  // Build comprehensive unit regex from textMatchToUnit
  const allUnitAliases = Object.keys(textMatchToUnit).join('|')

  let match

  // Pass 1: Extract structured format (e.g., "10 reps", "20 lbs", "12 / 10 / 8 reps", "12, 10, 8 reps", "10x reps")
  const structuredPattern = new RegExp(
    `((?:\\d+[.,]?\\d{0,2}\\s*[\\/\\,x]?\\s*)+)\\s*-?\\s*(${allUnitAliases})(?=[\\s.,;]|$)`,
    'gi'
  )
  while (unitExtractedCount < MAX_EXTRACTED_UNIT_COUNT && (match = structuredPattern.exec(normalizedText)) !== null) {
    const unitText = match[2]?.toLowerCase()
    const matchedSystemUnit = textMatchToUnit[unitText] || timeTextMatchToUnit[unitText]
    const matchedFullUnit = systemUnitToFullUnit[matchedSystemUnit]
    let value = match[1].trim() || 0

    //If distance type unit is already added, do not add a second distance unit
    const isDistanceUnitAdded = getIsDistanceUnitAdded({ unitToValue, matchedFullUnit })
    if (!isDistanceUnitAdded && matchedFullUnit) {
      unitExtractedCount++
      // Handle multi-value formats like "12 / 10 / 8"
      if (value.includes('/') || value.includes(',') || value.includes('x')) {
        value = value
          /* eslint-disable-next-line no-useless-escape */
          .split(/[\/\,x]/)
          .map((v) => parseFloat(v.trim()))
          .filter((v) => !isNaN(v))[0]
      } else {
        value = parseFloat(value)
      }
      if (!isNaN(value)) {
        unitToValue[matchedFullUnit] = true
      }
    }
    // console.log('Pass one text', normalizedText, 'unit to value', unitToValue)
  }

  // Pass 2: Extract key-value pairs (e.g., "reps: 12/10/8", "Reps 12, 10, 8", "Reps 12")

  const keyValuePattern = new RegExp(
    /* eslint-disable-next-line no-useless-escape */
    `\\b(${allUnitAliases})\\s*[\-\:\=]?\\s*((?:\\d+[.,]?\\d{0,2}(?:\\s*[\/\\,x]\\s*\\d+[.,]?\\d{0,2})*)+)`,
    'gi'
  )
  while (unitExtractedCount < MAX_EXTRACTED_UNIT_COUNT && (match = keyValuePattern.exec(normalizedText)) !== null) {
    const unitText = match[1].toLowerCase()
    const matchedSystemUnit = textMatchToUnit[unitText] || timeTextMatchToUnit[unitText] || allUnits[unitText]
    const matchedFullUnit = systemUnitToFullUnit[matchedSystemUnit]

    if (matchedFullUnit in unitToValue) {
      //If unit already accounted for, skip
      continue
    }

    let value = match[2].trim() || 0

    const isDistanceUnitAdded = getIsDistanceUnitAdded({ unitToValue, matchedFullUnit })
    if (!isDistanceUnitAdded && matchedFullUnit) {
      unitExtractedCount++
      // Handle multi-value formats
      if (value.includes('/') || value.includes(',') || value.includes('x')) {
        value = value
          /* eslint-disable-next-line no-useless-escape */
          .split(/[\/\,x]/)
          .map((v) => parseFloat(v.trim()))
          .filter((v) => !isNaN(v))[0]
      }
      if (!isNaN(value)) {
        unitToValue[matchedFullUnit] = true
      }
    }
    // console.log('Pass 2 text', normalizedText, 'unit to value', unitToValue)
  }

  // Pass 3: Look for key words that reps
  const repsPattern = new RegExp(`\\b(amraps?|reps?)\\b`, 'gi')
  if (unitExtractedCount < MAX_EXTRACTED_UNIT_COUNT && repsPattern.test(normalizedText)) {
    if (!unitToValue[allUnits.reps]) {
      //Add if not already added
      unitExtractedCount++
      unitToValue[allUnits.reps] = true
      // console.log('Pass 3 text', normalizedText, 'unit to value', unitToValue)
    }
  }

  // Pass 4: Look for key words that imply weight
  const weightPattern = new RegExp(`\\b(weights?|dumbbells?|kettlebells?|barbells?)\\b`, 'gi')
  if (unitExtractedCount < MAX_EXTRACTED_UNIT_COUNT && weightPattern.test(normalizedText)) {
    if (!unitToValue[allUnits['lb/kg']]) {
      //Add if not already added
      unitExtractedCount++
      unitToValue[allUnits['lb/kg']] = true
      // console.log('Pass 4 text', normalizedText, 'unit to value', unitToValue)
    }
  }

  // Pass 5: If no matches yet: Take first number found. Set it equal to reps as fallback
  const generalNumberPattern = new RegExp(`\\d+[.,]?\\d{0,2}`, 'gi') //Match any number
  if (unitExtractedCount === 0) {
    if ((match = generalNumberPattern.exec(normalizedText)) !== null) {
      // console.log('match in pass 3', match)
      let value = parseFloat(match[0] || 0)
      // console.log(" extractUnitValuesFromText ~ value in pass 3:", value)
      if (!isNaN(value)) {
        unitToValue[allUnits.reps] = true
      }
    }
    // console.log('Pass 5 text', normalizedText, 'unit to value', unitToValue)
  }

  return { unitToValueFromInstr: unitToValue }
}

export const getDefaultUnitsOfExFromInstr = ({ unitsOfEx, orgExUnits, instructions, exTimer, exHasStopWatch }) => {
  const fromExLibText = 'Unit set in Exercise Library'
  const fromInstrText = 'Interpreted from instructions'
  const fromTimerText = 'Exercise has a timer or stopwatch'

  if (!isEmpty(unitsOfEx)) {
    return { defaultUnits: unitsOfEx, interpFrom: {} }
  } else if (!isEmpty(orgExUnits)) {
    if (orgExUnits?.length === 2) {
      const firstUnit = orgExUnits?.[0]
      const secondUnit = orgExUnits?.[1]
      return { defaultUnits: orgExUnits, interpFrom: { [firstUnit]: fromExLibText, [secondUnit]: fromExLibText } }
    } else {
      // only 1 orgExUnit, try to populate second unit from instructions
      const { unitToValueFromInstr } = extractUnitValuesFromText({
        text: instructions,
        exTimer,
        exHasStopWatch,
      })
      const firstOrgExUnit = orgExUnits?.[0]
      const unitsFromInstr = getDefaultUnitsFromInstr(unitToValueFromInstr).filter((u) => u !== firstOrgExUnit)

      let defaultUnits = [firstOrgExUnit]
      let interpFrom = { [firstOrgExUnit]: fromExLibText }
      const firstUnitFromInstr = unitsFromInstr?.[0]
      if (firstUnitFromInstr) {
        defaultUnits.push(firstUnitFromInstr)
        interpFrom[firstUnitFromInstr] =
          firstUnitFromInstr === allUnits.time && (exTimer || exHasStopWatch) ? fromTimerText : fromInstrText
      }

      return {
        defaultUnits: sortUnitsByPriority(defaultUnits),
        interpFrom,
      }
    }
  } else {
    // units not set on workout exercise or org exercise
    // try to populate units from instructions
    const { unitToValueFromInstr } = extractUnitValuesFromText({
      text: instructions,
      exTimer,
      exHasStopWatch,
    })
    const unitsFromInstr = getDefaultUnitsFromInstr(unitToValueFromInstr)
    let interpFrom = {}
    unitsFromInstr.forEach(
      (u) => (interpFrom[u] = u === allUnits.time && (exTimer || exHasStopWatch) ? fromTimerText : fromInstrText)
    )
    return { defaultUnits: unitsFromInstr, interpFrom }
  }
}
